import { Loader2, ShieldOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from '@/components/layout/app-shell';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { AuditPage } from '@/pages/admin/audit-page';
import { RolesPage } from '@/pages/admin/roles-page';
import { TenantPage } from '@/pages/admin/tenant-page';
import { TicketConfigPage } from '@/pages/admin/ticket-config-page';
import { CustomerDetailPage } from '@/pages/customers/customer-detail-page';
import { CustomersPage } from '@/pages/customers/customers-page';
import { PortalCompanyPage, PortalProfilePage, RegisterPage } from '@/pages/portal/portal-account';
import { PortalHomePage, PortalNewTicketPage, PortalTicketPage } from '@/pages/portal/portal-pages';
import { PortalShell } from '@/pages/portal/portal-shell';
import { NewTicketPage } from '@/pages/tickets/new-ticket-page';
import { TicketDetailPage } from '@/pages/tickets/ticket-detail-page';
import { TicketsPage } from '@/pages/tickets/tickets-page';
import { FormBuilderPage } from '@/pages/forms/form-builder-page';
import { FormsPage, SubmissionsPage } from '@/pages/forms/forms-page';
import { ConfirmSubmissionPage, PublicFormPage } from '@/pages/forms/public-form-page';
import { PortalFormPage } from '@/pages/portal/portal-forms';
import { DeviceDetailPage, DevicesPage } from '@/pages/repairs/devices-pages';
import { NewRepairPage } from '@/pages/repairs/new-repair-page';
import { RepairDetailPage } from '@/pages/repairs/repair-detail-page';
import { RepairsPage } from '@/pages/repairs/repairs-page';
import { PortalRepairPage, PortalRepairsPage } from '@/pages/portal/portal-repairs';
import { M365Page } from '@/pages/admin/m365-page';
import { CalendarPage } from '@/pages/calendar/calendar-page';
import { ZabbixPage } from '@/pages/admin/zabbix-page';
import { MonitoringPage } from '@/pages/monitoring/monitoring-page';
import { ContractsPage } from '@/pages/contracts/contracts-page';
import { AutomationsPage } from '@/pages/admin/automations-page';
import { MailSettingsPage } from '@/pages/admin/mail-page';
import { ReportsPage } from '@/pages/reports/reports-page';
import { PublicRepairStatusPage } from '@/pages/repairs/public-status-page';
import { GuestTicketPage } from '@/pages/tickets/guest-ticket-page';
import { GuestRepairPage } from '@/pages/repairs/guest-repair-page';
import { MonitorPage } from '@/pages/monitor-page';
import { MonitorsPage } from '@/pages/admin/monitors-page';
import { MailTemplatesPage } from '@/pages/admin/mail-templates-page';
import { TeamsPage } from '@/pages/admin/teams-page';
import { WhatsAppPage } from '@/pages/admin/whatsapp-page';
import { PowerbirdPage } from '@/pages/admin/powerbird-page';
import { BillingPage } from '@/pages/billing/billing-page';
import { PrivacyPage } from '@/pages/admin/privacy-page';
import { LegalAdminPage } from '@/pages/admin/legal-page';
import { LegalPage } from '@/pages/legal-page';
import { PrintersPage } from '@/pages/admin/printers-page';
import { RatesPage } from '@/pages/admin/rates-page';
import { UsersPage } from '@/pages/admin/users-page';
import { ForgotPasswordPage, LoginPage, SetPasswordPage, VerifyEmailPage } from '@/pages/auth-pages';
import { DashboardPage } from '@/pages/dashboard-page';
import { AppsPage } from '@/pages/platform/apps-page';
import { TenantsPage } from '@/pages/platform/tenants-page';
import { ProfilePage } from '@/pages/profile-page';

function FullscreenLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullscreenLoader />;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

function Message({ title, text }: { title: string; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
      <ShieldOff className="size-10 text-muted-foreground" />
      <h1 className="text-xl font-semibold">{title}</h1>
      <p className="max-w-md text-sm text-muted-foreground">{text}</p>
      <Button asChild variant="outline" className="mt-2">
        <Link to="/">Zur Startseite</Link>
      </Button>
    </div>
  );
}

function Guard({ permission, platform, children }: { permission: string; platform?: boolean; children: ReactNode }) {
  const { can, me } = useAuth();
  if (!can(permission) || (platform && !me?.tenant.isPlatform)) {
    return <Message title="Keine Berechtigung" text="Für diesen Bereich fehlen Ihnen die nötigen Rechte. Wenden Sie sich an Ihren Administrator." />;
  }
  return <>{children}</>;
}

/** Startseite: Kunden ins Portal, Mitarbeiter aufs Dashboard bzw. ins Profil. */
function Home() {
  const { can, me } = useAuth();
  if (me?.customer) return <Navigate to="/portal" replace />;
  return can('dashboard.view') ? <DashboardPage /> : <Navigate to="/profile" replace />;
}

/** Bereich für Mitarbeiter: Portalbenutzer werden ins Kundenportal geleitet. */
function StaffOnly({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  if (me?.customer) return <Navigate to="/portal" replace />;
  return <>{children}</>;
}

function PortalOnly({ children }: { children: ReactNode }) {
  const { me, can } = useAuth();
  if (!me?.customer || !can('portal.access')) {
    return <Message title="Kein Kundenzugang" text="Das Kundenportal steht nur Kunden mit Portalzugang zur Verfügung." />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/f/confirm" element={<ConfirmSubmissionPage />} />
        <Route path="/f/:slug" element={<PublicFormPage />} />
        <Route path="/status/:token" element={<PublicRepairStatusPage />} />
        <Route path="/t/:token" element={<GuestTicketPage />} />
        <Route path="/r/:token" element={<GuestRepairPage />} />
        <Route path="/legal/:kind" element={<LegalPage />} />
        <Route path="/monitor/:token" element={<MonitorPage />} />
        <Route
          path="/portal"
          element={
            <RequireAuth>
              <PortalOnly>
                <PortalShell />
              </PortalOnly>
            </RequireAuth>
          }
        >
          <Route index element={<PortalHomePage />} />
          <Route path="new" element={<PortalNewTicketPage />} />
          <Route path="tickets/:id" element={<PortalTicketPage />} />
          <Route path="profile" element={<PortalProfilePage />} />
          <Route path="company" element={<PortalCompanyPage />} />
          <Route path="repairs" element={<PortalRepairsPage />} />
          <Route path="forms/:slug" element={<PortalFormPage />} />
          <Route path="repairs/:id" element={<PortalRepairPage />} />
        </Route>
        <Route
          element={
            <RequireAuth>
              <StaffOnly>
                <AppShell />
              </StaffOnly>
            </RequireAuth>
          }
        >
          <Route index element={<Home />} />
          <Route path="tickets" element={<Guard permission="tickets.read"><TicketsPage /></Guard>} />
          <Route path="tickets/new" element={<Guard permission="tickets.create"><NewTicketPage /></Guard>} />
          <Route path="tickets/:id" element={<Guard permission="tickets.read"><TicketDetailPage /></Guard>} />
          <Route path="customers" element={<Guard permission="customers.read"><CustomersPage /></Guard>} />
          <Route path="customers/:id" element={<Guard permission="customers.read"><CustomerDetailPage /></Guard>} />
          <Route path="repairs" element={<Guard permission="repairs.read"><RepairsPage /></Guard>} />
          <Route path="repairs/new" element={<Guard permission="repairs.intake"><NewRepairPage /></Guard>} />
          <Route path="repairs/:id" element={<Guard permission="repairs.read"><RepairDetailPage /></Guard>} />
          <Route path="devices" element={<Guard permission="devices.read"><DevicesPage /></Guard>} />
          <Route path="devices/:id" element={<Guard permission="devices.read"><DeviceDetailPage /></Guard>} />
          <Route path="forms" element={<Guard permission="forms.read"><FormsPage /></Guard>} />
          <Route path="forms/:id" element={<Guard permission="forms.manage"><FormBuilderPage /></Guard>} />
          <Route path="forms/:id/submissions" element={<Guard permission="forms.read"><SubmissionsPage /></Guard>} />
          <Route path="admin/ticket-config" element={<Guard permission="tickets.configure"><TicketConfigPage /></Guard>} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="admin/users" element={<Guard permission="admin.users.read"><UsersPage /></Guard>} />
          <Route path="admin/roles" element={<Guard permission="admin.users.read"><RolesPage /></Guard>} />
          <Route path="admin/tenant" element={<Guard permission="admin.tenant.manage"><TenantPage /></Guard>} />
          <Route path="calendar" element={<Guard permission="calendar.read"><CalendarPage /></Guard>} />
          <Route path="reports" element={<Guard permission="reports.read"><ReportsPage /></Guard>} />
          <Route path="billing" element={<Guard permission="billing.read"><BillingPage /></Guard>} />
          <Route path="contracts" element={<Guard permission="contracts.read"><ContractsPage /></Guard>} />
          <Route path="admin/integrations/teams" element={<Guard permission="integrations.manage"><TeamsPage /></Guard>} />
          <Route path="admin/integrations/whatsapp" element={<Guard permission="integrations.manage"><WhatsAppPage /></Guard>} />
          <Route path="admin/integrations/powerbird" element={<Guard permission="integrations.manage"><PowerbirdPage /></Guard>} />
          <Route path="admin/mail-templates" element={<Guard permission="admin.tenant.manage"><MailTemplatesPage /></Guard>} />
          <Route path="admin/monitors" element={<Guard permission="admin.tenant.manage"><MonitorsPage /></Guard>} />
          <Route path="admin/rates" element={<Guard permission="admin.users.manage"><RatesPage /></Guard>} />
          <Route path="admin/printers" element={<Guard permission="admin.tenant.manage"><PrintersPage /></Guard>} />
          <Route path="admin/privacy" element={<Guard permission="admin.tenant.manage"><PrivacyPage /></Guard>} />
          <Route path="admin/legal" element={<Guard permission="admin.tenant.manage"><LegalAdminPage /></Guard>} />
          <Route path="admin/mail" element={<Guard permission="admin.tenant.manage"><MailSettingsPage /></Guard>} />
          <Route path="admin/automations" element={<Guard permission="automations.manage"><AutomationsPage /></Guard>} />
          <Route path="monitoring" element={<Guard permission="monitoring.read"><MonitoringPage /></Guard>} />
          <Route path="admin/integrations/monitoring" element={<Guard permission="integrations.manage"><ZabbixPage /></Guard>} />
          <Route path="admin/integrations/zabbix" element={<Navigate to="/admin/integrations/monitoring" replace />} />
          <Route path="admin/integrations/m365" element={<Guard permission="integrations.manage"><M365Page /></Guard>} />
          <Route path="admin/audit" element={<Guard permission="admin.audit.read"><AuditPage /></Guard>} />
          <Route path="platform/tenants" element={<Guard permission="platform.tenants.manage" platform><TenantsPage /></Guard>} />
          <Route path="platform/apps" element={<Guard permission="platform.tenants.manage" platform><AppsPage /></Guard>} />
          <Route path="*" element={<Message title="Seite nicht gefunden" text="Die aufgerufene Seite existiert nicht." />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
